/**
 * @file documents.api.js
 * @module DocumentsAPI
 * @description HTTP API wrapper for document management endpoints. Methods return Axios promises.
 */

import { axiosInstance } from "./axios"

export const DocumentsAPI = {

  /**
   * Retrieve documents with pagination and optional query.
   * @param {number} limit
   * @param {number} page
   * @param {string} [query=""]
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to documents list.
   */
  getDocuments: (limit, page, query = "", token = "") => {
    return axiosInstance(token).get(
      `/documents?limit=${limit}&page=${page}${query ? `&${query}` : ""}`
    );
  },

  /**
   * Create a new document.
   * @param {Object} document
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to created document.
   */
  createDocument: (document, token = "") => {
    return axiosInstance(token).post(`/documents`, document);
  },

  /**
   * Get a document by id.
   * @param {string} documentId
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to document data.
   */
  getDocumentById: (documentId, token = "") => {
    return axiosInstance(token).get(`/documents/${documentId}`);
  },

  /**
   * Update a document by id.
   * @param {string} documentId
   * @param {Object} document
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to updated document.
   */
  updateDocumentById: (documentId, document, token = "") => {
    return axiosInstance(token).put(`/documents/${documentId}`, document);
  },

  /**
   * Patch attributes of a document by id.
   * @param {string} documentId
   * @param {Object} document
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to patched document.
   */
  updateAnyAttributeById: (documentId, document, token = "") => {
    return axiosInstance(token).patch(`/documents/${documentId}`, document);
  },

  /**
   * Delete a document by id.
   * @param {string} documentId
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to deletion result.
   */
  deleteDocumentById: (documentId, token = "") => {
    return axiosInstance(token).delete(`/documents/${documentId}`);
  },

  /**
   * Delete many documents by payload.
   * @param {Array|Object} documents
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to deletion result.
   */
  deleteManyDocumentsById: (documents, token = "") => {
    return axiosInstance(token).post(`/bulk-delete-documents`, {
      documents: documents
    });
  },

  /**
   * Check if document slug is duplicate for an app.
   * @param {string} documentSlug
   * @param {string} app
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to boolean/result.
   */
  checkDocumentSlugIsDuplicate: (documentSlug, app, token = "") => {
    return axiosInstance(token).post(`/documents/${documentSlug}/isDuplicate`, {
      app: app
    });
  },

  /**
   * Get last release by app.
   * @param {string} app - App identifier.
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to latest release info.
   */
  getLastReleaseByApp: (app, token = "") => {
    return axiosInstance(token).get(`/latest-version/${app}`);
  },

  /**
   * Get releases for an app.
   * @param {string} app - App identifier.
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to releases list.
   */
  getReleasesByApp: (app, token = "") => {
    return axiosInstance(token).get(`/releases/${app}`);
  },

  /**
   * Get current release by app.
   * @param {string} app - App identifier.
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to current release.
   */
  getCurrentReleaseByApp: (app, token = "") => {
    return axiosInstance(token).get(`/current-version/${app}`);
  },

  /**
   * Mark a release as watched for a user.
   * @param {string} app - App identifier.
   * @param {string|number} releaseVersion - Release version identifier.
   * @param {Object} payload - Payload to send when marking as watched.
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to the operation result.
   */
  markReleaseAsWatched: (app, releaseVersion, payload, token = "") => {
    return axiosInstance(token).put(
      `/apps/${app}/releases/${releaseVersion}/mark-as-read`,
      payload
    );
  },

  /**
   * Check if a user viewed a release.
   * @param {string} app - App identifier.
   * @param {string|number} releaseVersion - Release version identifier.
   * @param {string} userId - User identifier.
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to boolean/result.
   */
  checkViewedRelease: (app, releaseVersion, userId, token = "") => {
    return axiosInstance(token).get(
      `/apps/${app}/releases/${releaseVersion}/users/${userId}/check-viewed-release`
    );
  },

  /**
   * Get documents options for an app and search term.
   * @param {string} app - App identifier.
   * @param {string} search - Search term.
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to options list.
   */
  getDocumentsOptions: (app, search, token = "") => {
    // fix typo in route name
    return axiosInstance(token).get(`/search-documents-options?app=${app}&search=${search}`);
  },

  /**
   * Search documents by criteria.
   * @param {Object} searchCriteria - Search payload containing filters.
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to search results.
   */
  getDocumentsBySearchCriteria: (searchCriteria, token = "") => {
    return axiosInstance(token).post(`/documents-search`, searchCriteria);
  },

  /**
   * Get document details for app and slug.
   * @param {string} app - App identifier.
   * @param {string} documentSlug - Document slug.
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to document details.
   */
  getDocumentDetails: (app, documentSlug, token = "") => {
    return axiosInstance(token).get(`/document-details/${app}/${documentSlug}`);
  },

  /**
   * Get articles with pagination.
   * @param {string} [query=""] - Optional query string appended to the URL.
   * @param {number} limit - Number of articles per page.
   * @param {number} page - Page number.
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to articles list.
   */
  getArticles: (query = "", limit, page, token = "") => {
    return axiosInstance(token).get(
      `/articles?limit=${limit}&page=${page}${query ? `&${query}` : ""}`
    );
  },

  /**
   * Get support documents for an app/module.
   * @param {string} app - App identifier.
   * @param {string} module - Module identifier.
   * @param {string} [token=""]
   * @returns {Promise} Axios promise resolving to support documents.
   */
  getSupportDocuments: (app, module, token = "") => {
    return axiosInstance(token).get(`/apps/${app}/modules/${module}/help`);
  },
}
